import { uint8ArrayToBase64 } from "@utils/array-polyfills";
import { round } from "es-toolkit";

/** Used to calculate file size in byte, megaByte and gigaByte */
export const FILE_SIZE_MULTIPLIER = 1024;

/* eslint-disable @typescript-eslint/no-magic-numbers */
export const MAGIC_FILE_HEADERS = {
  // Blink scans are SQLite files
  // https://www.sqlite.org/fileformat.html#magic_header_string
  gls: [0x53, 0x51, 0x4c, 0x69, 0x74, 0x65, 0x20, 0x66, 0x6f, 0x72, 0x6d, 0x61, 0x74, 0x20, 0x33, 0x00],
  // Orbis files are SQLite files
  geoslam: [0x53, 0x51, 0x4c, 0x69, 0x74, 0x65, 0x20, 0x66, 0x6f, 0x72, 0x6d, 0x61, 0x74, 0x20, 0x33, 0x00],
  jpg: [0xff, 0xd8, 0xff],
  jpeg: [0xff, 0xd8, 0xff],
  // E57 header ASTM-E57
  e57: [0x41, 0x53, 0x54, 0x4d, 0x2d, 0x45, 0x35, 0x37]
  // Any other binary file types could be added in the future.
  // https://en.wikipedia.org/wiki/List_of_file_signatures
  // gif: [0x47, 0x49, 0x46, 0x38],
  // png: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
};
/* eslint-enable @typescript-eslint/no-magic-numbers */

/**
 * Check the extension of the file if it's valid or not
 *
 * @param file The file to check
 * @param validExtensions Array of valid extensions, or empty array to allow all extensions.
 * @returns true if the file has a supported format
 */
export function isValidFileExtension(file, validExtensions) {
  if (!validExtensions.length) {
    return true;
  }
  const extension = getFileExtension(file.name)?.toLowerCase();
  return !!extension && validExtensions.includes(extension);
}

/**
 * Converts a file size in bytes to a nice string representation like "21.5 MB".
 * @param bytes File size in bytes.
 */
export function bytesToSizeString(bytes) {
  const sizeUnitMap = {
    Bytes: 1,
    kB: 1024,
    MB: 1048576,
    GB: 1073741824,
    TB: 1099511627776
  };
  let unit;
  if (Math.abs(bytes) === 1) {
    return `${bytes} Byte`;
  } else if (Math.abs(bytes) < sizeUnitMap.kB) {
    return `${bytes} Bytes`;
  } else if (Math.abs(bytes) < sizeUnitMap.MB) {
    unit = "kB";
  } else if (Math.abs(bytes) < sizeUnitMap.GB) {
    unit = "MB";
  } else if (Math.abs(bytes) < sizeUnitMap.TB) {
    unit = "GB";
  } else {
    unit = "TB";
  }
  const convertedSize = bytes / sizeUnitMap[unit];
  return `${round(convertedSize, 1)} ${unit}`;
}

/** Returns mega bytes after converting from bytes */
export function bytesToMegaBytes(bytes) {
  const megaBytes = bytes / (FILE_SIZE_MULTIPLIER * FILE_SIZE_MULTIPLIER);
  return megaBytes.toFixed(2);
}

/**
 * Sort files by name
 *
 * @param files The array of file
 * @returns Sorted array of the files
 */
export function sortFiles(files) {
  return files.sort((a, b) => {
    return a.name.localeCompare(b.name);
  });
}

/**
 * @param files List of files to check.
 * @param shouldIgnoreCase True (default) to ignore case when comparing file names.
 * @returns The set of files with duplicate names.
 */
export function getFilesWithDuplicateNames(files, shouldIgnoreCase = true) {
  const fileNames = {};
  for (const file of files) {
    const fileName = shouldIgnoreCase ? file.name.toLocaleLowerCase("en") : file.name;
    fileNames[fileName] = (fileNames[fileName] ?? 0) + 1;
  }
  const duplicatedFiles = new Set();
  for (const file of files) {
    const fileName = shouldIgnoreCase ? file.name.toLocaleLowerCase("en") : file.name;
    if (fileNames[fileName] > 1) {
      duplicatedFiles.add(file);
    }
  }
  return duplicatedFiles;
}

// Contains the supported image file extensions
const IMAGE_FILE_TYPES = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "avif", "svg", "apng"];

/**
 * Check if the provided file extension is an image file type.
 *
 * @param fileExtension - The file extension to check.
 * @returns boolean - true if the file is an image, false otherwise.
 */
export function isImageFileType(fileExtension) {
  return IMAGE_FILE_TYPES.includes(fileExtension.toLowerCase());
}

/**
 * Extracts the file extension from a given file name.
 *
 * @param fileName The full name of the file (including the extension).
 * @returns The file extension in lowercase (e.g. 'jpg', 'png'), or an empty string if there is no extension.
 */
export function getFileExtension(fileName) {
  const trimmedFileName = fileName.trim();
  if (trimmedFileName.startsWith(".") && trimmedFileName.indexOf(".", 1) === -1) {
    return "";
  }
  const fileNameParts = trimmedFileName.split(".");
  return fileNameParts.length > 1 ? (fileNameParts.pop() ?? "").toLowerCase() : "";
}

/**
 * If the filename has an extension, return the filename without extension
 * else return the filename as is.
 * @param fileName
 */
export function getFileNameWithoutExtension(fileName) {
  const trimmedFileName = fileName.trim();
  if (trimmedFileName === "") {
    return trimmedFileName;
  }
  if (trimmedFileName.startsWith(".") && trimmedFileName.indexOf(".", 1) === -1) {
    return trimmedFileName;
  }
  const lastDotIndex = trimmedFileName.lastIndexOf(".");
  if (lastDotIndex <= 0) {
    return trimmedFileName;
  }
  return trimmedFileName.substring(0, lastDotIndex);
}

/**
 * @param byteArray An array of bytes, like [0x00, 0xFF].
 * @returns A hex string in lower case, like "00ff".
 */
export function bytesToHex(byteArray) {
  return Array.from(byteArray)
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  .map(b => b.toString(16).padStart(2, "0")).join("");
}

/**
 * For some known file extensions, checks if the file has the expected header.
 * For other file extensions, this function is a NOP.
 * @param file File or FileExt object.
 * @param areUnknownFilesAllowed If true, files with unknown headers are ignored and no error is thrown (default: true).
 * @throws {Error} if the header is invalid or could not be read.
 */
export async function checkMagicFileHeader(file, areUnknownFilesAllowed = true) {
  const extension = getFileExtension(file.name)?.toLowerCase();
  const expectedHeader = MAGIC_FILE_HEADERS[extension];
  if (!extension || !expectedHeader) {
    if (areUnknownFilesAllowed) {
      // If unknown files are allowed, we don't check the header.
      return;
    }
    if (!extension) {
      throw new Error("Missing file extension.");
    }
    throw new Error(`Unknown file extension: ${extension}`);
  }
  const numBytes = expectedHeader.length;
  const buffer = await file.slice(0, numBytes).arrayBuffer();
  const actualHeaderHex = bytesToHex(new Uint8Array(buffer));
  const expectedHeaderHex = bytesToHex(expectedHeader);
  if (actualHeaderHex !== expectedHeaderHex) {
    throw new Error("Invalid file contents.");
  }
}

/**
 * @param file File or FileExt object.
 * @returns File contents as Base64 string.
 */
async function fileToBase64(file) {
  // Read the blob as binary data and convert to base64
  const arrayBuffer = await file.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);
  return uint8ArrayToBase64(uint8Array);
}

/**
 * @param files Map from filename to File or FileExt object.
 * @returns Map from filename to File contents as Base64 string.
 */
export async function filesToBase64(files) {
  const filesBase64 = {};
  const promises = [];

  // Read all files in parallel.
  for (const [name, file] of Object.entries(files)) {
    promises.push(fileToBase64(file).then(base64 => {
      filesBase64[name] = base64;
    }));
  }
  await Promise.all(promises);
  return filesBase64;
}

/** @returns Combined string with file name and size, for using e.g. as Record or Map key. */
export function uploadedFileHash(fileName, fileSize) {
  return `${fileName}_${fileSize}`;
}